import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ConfirmationService } from 'primeng/api';
import { forkJoin, Observable, of } from 'rxjs';
import { UsStates } from 'src/app/_data/us-states';
import { WindowsTimeZoneToIana } from 'src/app/_data/time-zones';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { GlobalService } from 'src/app/_services/global.service';
import { LocationsService } from 'src/app/_services/locations.service';
import { UserService } from 'src/app/_services/user.service';
import { environment } from 'src/environments/environment';

interface TimezoneOption {
  label: string;
  value: string;      // Windows tzName (e.g. "Central Standard Time") — matches CountryTimeZone.TimeZones.TZName,
                       // used as-is for both business/timezoneoptions (preselect) and business/timezone (save).
  utcOffset: string;   // e.g. "-06:00"
  countryCode: string;
}

interface TimezoneGroup {
  label: string;
  items: TimezoneOption[];
}

@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.scss']
})
export class GeneralComponent implements OnInit {

  _globals = GlobalService
  editform: FormGroup
  usaStates = UsStates
  showCityWait = false
  @Input() isViewOnly: boolean = false
  selectedState: any;
  emailValidatorString = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  @Input() singupBusinessConfig: boolean = true;
  selectedTimeZone: TimezoneOption | null = null;
  timezoneGroups: TimezoneGroup[] = [];
  lastConfirmedTimeZone: TimezoneOption | null = null;
  isSavingTimeZone = false;
  // Gates the whole widget (toolbar + form), not just the Time Zone field — the form reads
  // _globals.userInfo synchronously in initForm()/initFormEdit(), but the Time Zone dropdown's
  // correct default depends on the async business/timezoneoptions call, so the simplest way to avoid
  // a flash of the wrong default is to hold the entire widget behind one skeleton until that
  // resolves, rather than only skeleton-ing the Time Zone field in isolation.
  isLoading = true;

  private static readonly COUNTRY_NAMES: { [code: string]: string } = {
    US: 'United States',
    PK: 'Pakistan',
  };

  // Used only if GetTimeZonesApi errors, or the CountryTimeZone container isn't seeded yet — keeps
  // the picker usable (matching the real US+PK zones/offsets) instead of empty. Same set as the
  // admin picker (bizz-customer-personal-info.component.ts) for consistency.
  private static readonly FALLBACK_TIMEZONES: Array<{ tzName: string; countryName: string; countryCode: string; utcOffset: string }> = [
    { tzName: 'SA Western Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-04:00' },
    { tzName: 'US Mountain Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-07:00' },
    { tzName: 'Alaskan Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-09:00' },
    { tzName: 'Hawaiian Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-10:00' },
    { tzName: 'Eastern Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-05:00' },
    { tzName: 'Central Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-06:00' },
    { tzName: 'Mountain Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-07:00' },
    { tzName: 'Pacific Standard Time', countryName: 'United States', countryCode: 'US', utcOffset: '-08:00' },
    { tzName: 'Pakistan Standard Time', countryName: 'Pakistan', countryCode: 'PK', utcOffset: '+05:00' },
  ];

  constructor(
    private fb: FormBuilder,
    public _abd: AppBaseDataService,
    public _businessSetup: BusinessSetupService,
    public _locationService: LocationsService,
    private _spinner: NgxSpinnerService,
    private _connMessageService: ConnMessageService,
    private _userService: UserService,
    private _confirmationService: ConfirmationService,
    ) { }

  ngOnInit(): void {
    this.initForm()
    this.initFormEdit()
    this.preselectSavedIndustry()
    let obj ={
      id:this._globals.userInfo.businessSetup[0]?.industry
    }
    this.refreshUserThenLoadTimezoneOptions()
    // this.selectIndustryType(obj)
  }

  // GlobalService.userInfo can be a stale snapshot from an earlier login/session — its embedded
  // active timezone (businessSetup[0].timeZones[]/timeZone) is only as fresh as that snapshot, so a
  // zone changed via business/timezone since then wouldn't show up here without a fresh fetch. Get
  // the current user (GETUSERBYUSERNAME, same call the rest of the app uses) first, re-sync
  // GlobalService.userInfo from it, then read the embedded zone off that fresh copy.
  private refreshUserThenLoadTimezoneOptions(): void {
    const username = this._globals.userInfo?.username
    if (!username) {
      this.loadTimezoneOptions()
      return
    }

    this._userService.getUserByUserName(username).subscribe({
      next: (res) => {
        if (res?.success && res?.data) {
          this._globals.userInfo = res.data
        }
        this.loadTimezoneOptions()
      },
      error: () => this.loadTimezoneOptions()
    })
  }

  // business/timezoneoptions returns the selectable zones for the logged-in user's own business —
  // each entry carries tzName (Windows id)/utcOffset/countryCode/isActive, sourced from the
  // CountryTimeZone container. Same call the admin picker uses (bizz-customer-personal-info),
  // just without a businessName override so it targets the caller's own business.
  //
  // The already-loaded user object (GlobalService.userInfo) embeds the real active zone directly —
  // at businessSetup[0].timeZones[] (isActive flag) and again at the root timeZone object — and
  // that embedded value is authoritative. business/timezoneoptions' own isActive flag has been
  // observed not reflecting the actual active zone, so it's only used as a fallback when nothing is
  // embedded (e.g. a business that hasn't set one yet).
  private loadTimezoneOptions(): void {
    this.isLoading = true
    const embedded = this.getEmbeddedActiveTimeZone()

    this._businessSetup.getBusinessTimeZoneOptions().subscribe({
      next: (res) => {
        const zones = this.extractZones(res)
        this.timezoneGroups = zones.length ? this.groupZonesByCountry(zones) : this.buildFallbackTimezoneGroups()
        this.applyActiveTimeZone(embedded, zones)
      },
      error: () => {
        this.timezoneGroups = this.buildFallbackTimezoneGroups()
        this.applyActiveTimeZone(embedded, [])
      }
    })
  }

  // PrimeNG's p-dropdown [options] setter eagerly re-resolves the current selection against its own
  // (still-stale) internal value the instant [options] changes, and pushes whatever it resolves
  // (first item, or null) back into the bound ngModel via onModelChange — synchronously, in the same
  // change-detection pass. If [options] and [(ngModel)] are both updated in that same pass (as they
  // are here, right after the async response), that eager re-resolution runs BEFORE the fresh
  // ngModel value is written, sees a mismatch, and clobbers selectedTimeZone before it's even set.
  // Setting timezoneGroups now and selectedTimeZone on the next tick lets Angular finish processing
  // [options] (so the dropdown's internal option list is already correct) before ngModel writes the
  // real value, so the dropdown's own lookup succeeds instead of overwriting it.
  private applyActiveTimeZone(embedded: TimezoneOption | null, zones: any[]): void {
    let resolved: TimezoneOption | null
    if (embedded) {
      this.timezoneGroups = this.mergeOptionIntoGroups(this.timezoneGroups, embedded)
      resolved = this.findTimezoneOption(embedded.value) || embedded
    } else {
      const active = this.findActiveTimezone(zones)
      resolved = (active && this.findTimezoneOption(active)) || this.timezoneGroups[0]?.items[0] || null
    }

    setTimeout(() => {
      this.selectedTimeZone = resolved
      this.initialTimeZone = resolved
      this.syncActiveTimeZoneToGlobal(resolved)
      this.isLoading = false
    })
  }

  // businessSetup[0].timeZones[] (preferred — this is what business/timezone actually writes to) or
  // the root timeZone object are both already present on the user object fetched at login, so this
  // needs no network call and can't be stale relative to the last successful save.
  private getEmbeddedActiveTimeZone(): TimezoneOption | null {
    const businessSetup = this._globals.userInfo?.businessSetup?.[0]
    const fromArray = Array.isArray(businessSetup?.timeZones)
      ? businessSetup.timeZones.find((t: any) => t?.isActive === true || t?.IsActive === true)
      : null
    const source = fromArray || this._globals.userInfo?.timeZone

    const tzName = String(source?.tzName ?? source?.TzName ?? source?.TZName ?? '').trim()
    if (!tzName) return null

    const utcOffset = String(source?.utcOffset ?? source?.UtcOffset ?? source?.UTCOffset ?? '').trim()
    const countryCode = String(source?.countryCode ?? source?.CountryCode ?? '').trim()
    const offsetPrefix = this.formatUtcOffsetText(utcOffset)

    return {
      label: offsetPrefix ? `${offsetPrefix} ${tzName}` : tzName,
      value: tzName,
      utcOffset,
      countryCode,
    }
  }

  // Ensures the embedded active zone is selectable in the dropdown even if business/timezoneoptions
  // (or the fallback list) didn't happen to include it — without this, PrimeNG would render the
  // business's actual active zone as blank.
  private mergeOptionIntoGroups(groups: TimezoneGroup[], option: TimezoneOption): TimezoneGroup[] {
    if (groups.some((g) => g.items.some((i) => i.value === option.value))) {
      return groups
    }

    const groupLabel = GeneralComponent.COUNTRY_NAMES[option.countryCode.toUpperCase()] || option.countryCode || 'Other'
    const next = groups.map((g) => ({ label: g.label, items: [...g.items] }))
    const existingGroup = next.find((g) => g.label === groupLabel)
    if (existingGroup) {
      existingGroup.items.push(option)
    } else {
      next.push({ label: groupLabel, items: [option] })
    }

    return next.sort((a, b) => {
      if (a.label === 'United States') return -1
      if (b.label === 'United States') return 1
      return a.label.localeCompare(b.label)
    })
  }

  // Keeps GlobalService.activeBusinessTimeZoneIana (what JwtInterceptor puts in the X-Time-Zone
  // header) in step with the real active zone from business/timezoneoptions — falls back to the
  // Windows tzName itself if it isn't in the known map, rather than clobbering with something wrong.
  private syncActiveTimeZoneToGlobal(option: TimezoneOption | null): void {
    if (!option?.value) return
    GlobalService.activeBusinessTimeZoneIana = WindowsTimeZoneToIana[option.value] || option.value
  }

  private findActiveTimezone(zones: any[]): string | null {
    const active = zones.find((zone: any) =>
      zone?.isActive === true || zone?.IsActive === true ||
      zone?.isSelected === true || zone?.IsSelected === true ||
      zone?.isCurrent === true || zone?.IsCurrent === true
    )
    if (!active) return null

    const tzName = active?.tzName ?? active?.TZName ?? active?.TzName ?? active?.ZoneName ?? active?.zoneName ?? null
    return tzName ? String(tzName).trim() : null
  }

  private extractZones(res: any): any[] {
    const data = res?.data ?? res
    const list = data?.Zones ?? data?.zones ?? data
    return Array.isArray(list) ? list : []
  }

  private groupZonesByCountry(zones: any[]): TimezoneGroup[] {
    const byCountry = new Map<string, TimezoneGroup>()

    zones.forEach((zone) => {
      const tzName = String(zone?.tzName ?? zone?.TZName ?? zone?.TzName ?? zone?.ZoneName ?? zone?.zoneName ?? '').trim()
      if (!tzName) return

      const countryCode = String(zone?.countryCode ?? zone?.CountryCode ?? '').trim()
      const countryName = String(zone?.countryName ?? zone?.CountryName ?? '').trim()
        || GeneralComponent.COUNTRY_NAMES[countryCode.toUpperCase()]
        || countryCode
        || 'Other'
      const utcOffset = String(zone?.utcOffset ?? zone?.UTCOffset ?? zone?.UtcOffset ?? zone?.GmtOffset ?? zone?.gmtOffset ?? '').trim()

      if (!byCountry.has(countryName)) {
        byCountry.set(countryName, { label: countryName, items: [] })
      }

      const offsetPrefix = this.formatUtcOffsetText(utcOffset)
      byCountry.get(countryName)!.items.push({
        label: offsetPrefix ? `${offsetPrefix} ${tzName}` : tzName,
        value: tzName,
        utcOffset,
        countryCode,
      })
    })

    return Array.from(byCountry.values()).sort((a, b) => {
      if (a.label === 'United States') return -1
      if (b.label === 'United States') return 1
      return a.label.localeCompare(b.label)
    })
  }

  private formatUtcOffsetText(raw: any): string {
    if (raw === null || raw === undefined || raw === '') return ''

    const text = String(raw).trim()
    if (!text.includes(':') && Number.isFinite(Number(text))) {
      const totalMinutes = Math.round(Number(text) / 60)
      const sign = totalMinutes >= 0 ? '+' : '-'
      const abs = Math.abs(totalMinutes)
      const hh = String(Math.floor(abs / 60)).padStart(2, '0')
      const mm = String(abs % 60).padStart(2, '0')
      return `(UTC${sign}${hh}:${mm})`
    }

    const withSign = text.startsWith('+') || text.startsWith('-') ? `UTC${text}` : text
    return withSign.startsWith('(') ? withSign : `(${withSign})`
  }

  private buildFallbackTimezoneGroups(): TimezoneGroup[] {
    const byCountry = new Map<string, TimezoneGroup>()

    GeneralComponent.FALLBACK_TIMEZONES.forEach((tz) => {
      if (!byCountry.has(tz.countryName)) {
        byCountry.set(tz.countryName, { label: tz.countryName, items: [] })
      }
      const offsetPrefix = this.formatUtcOffsetText(tz.utcOffset)
      byCountry.get(tz.countryName)!.items.push({
        label: offsetPrefix ? `${offsetPrefix} ${tz.tzName}` : tz.tzName,
        value: tz.tzName,
        utcOffset: tz.utcOffset,
        countryCode: tz.countryCode,
      })
    })

    return Array.from(byCountry.values()).sort((a, b) => {
      if (a.label === 'United States') return -1
      if (b.label === 'United States') return 1
      return a.label.localeCompare(b.label)
    })
  }

  private findTimezoneOption(value: string): TimezoneOption | undefined {
    for (const group of this.timezoneGroups) {
      const match = group.items.find((i) => i.value === value)
      if (match) return match
    }
    return undefined
  }

  // Selection updates ngModel immediately (PrimeNG dropdown) — just confirms/reverts the in-form
  // pick here. The actual business/timezone save only happens from saveFn() below, and only if the
  // confirmed selection differs from what was loaded.
  initialTimeZone: TimezoneOption | null = null;
  onTimeZoneChange(newTimeZone: TimezoneOption) {
    const previous = this.selectedTimeZone
    if (!newTimeZone) return

    this._confirmationService.confirm({
      key: 'generalTimeZoneChange',
      message: `Change time zone to ${newTimeZone.label}?`,
      header: 'Change Time Zone',
      icon: 'pi pi-info-circle',
      accept: () => {
        this.selectedTimeZone = newTimeZone
      },
      reject: () => {
        this.selectedTimeZone = previous
      },
    })
  }

  ngOnDestroy() {
    this._locationService.blankActiveItem()
  }

  initFormEdit() {
    const st = this.usaStates.filter(s => s.abbreviation == this._globals.userInfo.businessSetup[0].state)
    this.selectedState = st[0];
  }

  emailFormatValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const pvalid = value.match(this.emailValidatorString)
      return pvalid ? null : { emailStrength: true };
    }
  }

  initForm() {
  
   if(this._globals.userInfo.businessSetup[0].address === "" && this._globals.userInfo.businessSetup[0].isFreeTrial === false ){
    this._globals.userInfo.businessSetup[0].address =  this._globals.userInfo.businessSetup[0]?.subscription?.transactionDetails?.billTo.address
    this._globals.userInfo.businessSetup[0].city =  this._globals.userInfo.businessSetup[0]?.subscription?.transactionDetails?.billTo.city
    this._globals.userInfo.businessSetup[0].zip =  this._globals.userInfo.businessSetup[0]?.subscription?.transactionDetails?.billTo.zip

   }

    this.editform = this.fb.group({
      companyName: [{ value:    this._globals.userInfo.businessSetup[0].businessDisplayName, disabled: this.isViewOnly }, Validators.required],
      companyAddress: [{value:  this._globals.userInfo.businessSetup[0].address, disabled: this.isViewOnly }, Validators.required],
      companyCity: [{value:     this._globals.userInfo.businessSetup[0].city, disabled: this.isViewOnly }, Validators.required],
      companyZip: [{value:      this._globals.userInfo.businessSetup[0].zip, disabled: this.isViewOnly },[Validators.required]],
      companyPhone: [{value:    this._globals.userInfo.businessSetup[0].phone, disabled: this.isViewOnly }, this._abd.phoneValidator()],
      companyEmail: [{value:    this._globals.userInfo.username, disabled: true },[Validators.required, this.emailFormatValidator()]],
    })
  }

  get f(){
    return this.editform.controls
  }

  // Saved industry is "Name || description"; match the name to a type so its button shows as selected.
  private preselectSavedIndustry(): void {
    const savedName = String(this._globals.userInfo?.businessSetup?.[0]?.industry ?? '').split(' || ')[0].trim()
    if (!savedName) return
    const select = (types: any[]) => {
      const match = (types ?? []).find(t => t.name === savedName)
      if (match) this._businessSetup.activeItem.industryType = match.id
    }
    if (this._abd.industryTypes?.length) {
      select(this._abd.industryTypes)
    } else {
      this._abd.getIndustryTypes().subscribe(types => select(types))
    }
  }

  // selectIndustryType(obj: any) {
  //   this._businessSetup.activeItem.industryType = obj.id
  // }

  zipInputEvent(value: string) {
    if (!value || value.replace(/\D/g, '').length !== 5) return
    this.showCityWait = true
    this._abd.getCityAndStateInfo(value).subscribe((res: any) => {
      this.showCityWait = false
      if (res.success && res.data.city != undefined) {
        const st = this.usaStates.filter(s => s.abbreviation == res.data.state)
        this.selectedState = st[0];
        this.editform.patchValue({ companyCity: res.data.city })
      } else {
        this.editform.patchValue({ companyCity: "Invalid zip code" })
        this.selectedState = this.usaStates[0]
      }
    })
  }

  get isTimeZoneDirty(): boolean {
    return this.selectedTimeZone?.value !== this.initialTimeZone?.value
  }

  saveFn(){
    this._businessSetup.activeItem.UserID =       this._globals.userInfo.userID
    this._businessSetup.activeItem.businessType = ""
    this._businessSetup.activeItem.ContactEmail = this._globals.userInfo.username

    // Same V2 payload shape the business-setup checklist saves: industry is "Name || description".
    const setup = this._globals.userInfo.businessSetup[0]
    const selectedType = this._abd.industryTypes.find(t => t.id == this._businessSetup.activeItem.industryType)
    const [savedName, ...savedDesc] = String(setup?.industry ?? '').split(' || ')
    const description = savedDesc.join(' || ').trim() || setup?.description || ''
    const industryName = selectedType?.name ?? savedName.trim()

    let bizInfoVariable = {
      businessDisplayName: this.f.companyName.value,
      industry: description ? `${industryName} || ${description}` : industryName,
      description,
      address: this.f.companyAddress.value,
      address2: setup?.address2 ?? '',
      city: this.f.companyCity.value,
      state: this.selectedState.abbreviation,
      zip: this.f.companyZip.value,
      country: setup?.country || 'US',
      phone: this.f.companyPhone.value,
      logo: setup?.logo ?? '',
      businessLogo: setup?.businessLogo ?? '',
      preferredShippingProvider: setup?.preferredShippingProvider ?? '',
      businessId: setup?.businessId
    }

    try{
      this._abd.showSpinner()
      // showSpinner:false on every intermediate call below — each of these normally auto-hides the
      // global spinner the instant IT finishes (BaseService.httpPost does this per-call, not per
      // workflow). Left at their defaults, the spinner disappeared right after EditBusinessInfo
      // succeeded, well before staff-user creation / user refetch / timezone save even started, so
      // the user could navigate away mid-save and silently lose the rest of this chain. This method
      // owns showSpinner()/hideSpinner() for the whole chain instead — hideSpinner() only runs once
      // everything (including the timezone save) is actually done, or on any failure along the way.
      this._businessSetup.saveBusinessInformationV2(bizInfoVariable, false).subscribe((res)=>{
        if(res.success){
          this._userService.createStaffUser_ForSetupOnly(JSON.stringify(this._businessSetup.activeItem), false).subscribe(res =>res )
          this._connMessageService.showToastMessage(`Business Information saved successfully!`, 'success')
          this._userService.getUserByUserName(this._globals.userInfo.username).subscribe(res1=>{
            if(res1.success){
              // Keep the spinner up until the business/timezone save (if any) actually finishes —
              // hiding it right after firing that request left the UI looking "done" while the
              // timezone POST (and its own follow-up refetch) was still in flight in the background.
              this.saveTimeZoneIfChanged().subscribe(() => {
                this._abd.hideSpinner()
              })
            } else {
              this._abd.hideSpinner()
            }
          })
        } else {
          this._connMessageService.showToastMessage(res?.message || 'Error saving business information!', 'error')
          this._abd.hideSpinner()
        }
      })
    }
    catch{
      this._connMessageService.showToastMessage(`Error saving business information!`, 'error')
      this._abd.hideSpinner()
    }
  }

  // Only calls business/timezone when the confirmed selection actually differs from what was loaded
  // — an unchanged timezone should never re-hit this endpoint just because Save was pressed.
  // Returns an Observable that completes once the save (and, if it happened, the follow-up user
  // refetch) is done, so callers can keep their own "saving..." UI up for the full duration instead
  // of it disappearing the instant this call is fired.
  private saveTimeZoneIfChanged(): Observable<void> {
    if (!this.isTimeZoneDirty || !this.selectedTimeZone) return of(undefined)

    const option = this.selectedTimeZone
    this.isSavingTimeZone = true
    return new Observable<void>((subscriber) => {
      this._businessSetup.saveBusinessTimeZone({
        tzName: option.value,
        utcOffset: option.utcOffset,
        countryCode: option.countryCode,
      }).subscribe({
        next: (res) => {
          this.isSavingTimeZone = false
          if (res?.success === false) {
            this._connMessageService.showToastMessage(res?.message || 'Time zone could not be saved.', 'error')
            subscriber.next()
            subscriber.complete()
            return
          }
          this.initialTimeZone = option
          this.syncActiveTimeZoneToGlobal(option)

          // Refresh GlobalService.userInfo from B2CGraphClientApi (GETUSERBYUSERNAME) so the embedded
          // active zone (businessSetup[0].timeZones[]/timeZone) matches what was just saved — other
          // consumers of GlobalService.userInfo shouldn't keep serving the pre-save snapshot.
          this._userService.getUserByUserName(this._globals.userInfo.username).subscribe({
            next: (userRes) => {
              if (userRes?.success && userRes?.data) {
                this._globals.userInfo = userRes.data
              }
              subscriber.next()
              subscriber.complete()
            },
            error: () => {
              subscriber.next()
              subscriber.complete()
            }
          })
        },
        error: () => {
          this.isSavingTimeZone = false
          this._connMessageService.showToastMessage('Time zone could not be saved.', 'error')
          subscriber.next()
          subscriber.complete()
        }
      })
    })
  }

  cancelButtonClick(){

  }

}
